import { assetUrl, checkIfChampionIsKaynTransformation, formatArenaPlacement } from '../../helper_functions/utilities';
export const LEGACY_ARENA_QUEUE_ID = 1700;
export const TRIOS_ARENA_QUEUE_ID = 1750;
export const ARENA_QUEUE_IDS = [LEGACY_ARENA_QUEUE_ID, TRIOS_ARENA_QUEUE_ID];

const QUEUE_LABELS = {
    400: 'Normal',
    420: 'Solo Ranked',
    440: 'Flex',
    450: 'ARAM',
    470: 'Twisted Treeline',
    480: 'Swift Play',
    490: 'Quick Play',
    700: 'Clash',
    710: 'Ranked 5s',
    720: 'ARAM Clash',
    900: 'ARURF',
    1020: 'One For All',
    1300: 'Nexus Blitz',
    1400: 'Ult Spellbook',
    1700: 'Arena',
    1750: 'Arena',
    1900: 'URF',
};

const ARENA_TEAM_NAMES = {
    1: 'Poro',
    2: 'Minion',
    3: 'Scuttle',
    4: 'Krug',
    5: 'Raptor',
    6: 'Sentinel',
    7: 'Wolf',
    8: 'Gromp',
};

const ROLE_LABELS = {
    TOP: 'Top',
    JUNGLE: 'Jungle',
    MIDDLE: 'Mid',
    BOTTOM: 'Bottom',
    UTILITY: 'Support',
};

const ROMAN_RANK_LABELS = {
    I: 'I',
    II: 'II',
    III: 'III',
    IV: 'IV',
};

function toNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function isArenaQueue(queueId) {
    return ARENA_QUEUE_IDS.includes(toNumber(queueId));
}

function isTriosArenaQueue(queueId) {
    return toNumber(queueId) === TRIOS_ARENA_QUEUE_ID;
}

function getArenaVictoryPlacement(queueId) {
    return isTriosArenaQueue(queueId) ? 3 : 4;
}

function formatDecimal(value, digits = 1) {
    const normalized = toNumber(value).toFixed(digits);
    return normalized.endsWith('.0') ? normalized.slice(0, -2) : normalized;
}

function formatDamageValue(value) {
    return `${formatDecimal(toNumber(value) / 1000)}k`;
}

function formatGoldValue(value) {
    return `${formatDecimal(toNumber(value) / 1000)}k`;
}

function formatPercent(value) {
    return Math.round(toNumber(value) * 100);
}

function toItemUrl(itemId) {
    return assetUrl(`/item_icons/${toNumber(itemId)}.png`);
}

function toSpellUrl(spellId) {
    return assetUrl(`/summoner_spell_icons/${toNumber(spellId)}.png`);
}

function toChampionUrl(championName) {
    return assetUrl(`/champion_icons/${championName}.png`);
}

function toAugmentUrl(augmentId) {
    return assetUrl(`/augment_icons/${toNumber(augmentId)}.png`);
}

function getRoleViewModel(teamPosition) {
    const normalizedPosition = String(teamPosition || '').toUpperCase();
    const label = ROLE_LABELS[normalizedPosition];

    if (!label) {
        return {
            roleKey: '',
            roleLabel: '',
            roleIconSrc: '',
        };
    }

    return {
        roleKey: normalizedPosition.toLowerCase(),
        roleLabel: label,
        roleIconSrc: `${process.env.PUBLIC_URL}/assets/role_icons/position-${normalizedPosition === 'UTILITY' ? 'utility' : normalizedPosition.toLowerCase()}-light.svg`,
    };
}

function normalizePosition(position) {
    const normalizedPosition = String(position || '').toUpperCase();

    if (normalizedPosition === 'SUPPORT') {
        return 'UTILITY';
    }

    return normalizedPosition;
}

function getRoleStatusLabel(player) {
    const assignedPosition = normalizePosition(player?.teamPosition);
    const actualPosition = normalizePosition(player?.individualPosition);

    if (!assignedPosition || !actualPosition || !ROLE_LABELS[assignedPosition] || !ROLE_LABELS[actualPosition]) {
        return '';
    }

    return assignedPosition === actualPosition ? '' : 'FILLED';
}

function toPrimaryRuneUrl(primaryStyle, primaryPerk) {
    return assetUrl(`/major_and_minor_rune_icons/trees/${primaryStyle}/slot_1/${primaryPerk}.png`);
}

function toSecondaryRuneUrl(secondaryStyle) {
    return assetUrl(`/major_and_minor_rune_icons/trees/${secondaryStyle}.png`);
}

function buildOrderedItems(player) {
    const orderedItems = [0, 0, 0, 0, 0, 0, 0];
    let nextIndex = 0;

    for (let index = 0; index <= 6; index += 1) {
        const itemId = toNumber(player?.[`item${index}`]);

        if (!itemId) {
            continue;
        }

        if (index === 6) {
            orderedItems[6] = itemId;
            continue;
        }

        orderedItems[nextIndex] = itemId;
        nextIndex += 1;
    }

    return {
        itemIds: orderedItems.slice(0, 6),
        trinketId: orderedItems[6],
        roleBoundItemId: toNumber(player?.roleBoundItem),
    };
}

function getHighestStreak(player) {
    if (toNumber(player?.pentaKills) > 0) {
        return { label: 'PENTA', style: 'penta' };
    }

    if (toNumber(player?.quadraKills) > 0) {
        return { label: 'QUADRA', style: 'quadra' };
    }

    return null;
}

function getEloString(player) {
    const elo = player?.wrgg?.elo;
    const tier = elo?.tier;
    const division = elo?.division;

    if (!tier || tier.toLowerCase() === 'unranked') {
        return 'UNRANKED';
    }

    const normalizedTier = String(tier).toUpperCase();
    const tierLabel = normalizedTier === 'GRANDMASTER'
        ? 'GRAND MASTER'
        : `${normalizedTier.charAt(0)}${normalizedTier.slice(1).toLowerCase()}`.toUpperCase();
    const divisionLabel = ROMAN_RANK_LABELS[String(division).toUpperCase()] || '';

    if (normalizedTier === 'MASTER' || normalizedTier === 'GRANDMASTER' || normalizedTier === 'CHALLENGER') {
        return tierLabel;
    }

    return `${tierLabel} ${divisionLabel}`.trim();
}

function getFocusTone(player, isArena, isRemake, arenaVictoryPlacement = 4) {
    if (isRemake) {
        return 'remake';
    }

    if (isArena) {
        return toNumber(player?.placement) <= arenaVictoryPlacement ? 'victory' : 'defeat';
    }

    return player?.win ? 'victory' : 'defeat';
}

function getArenaPlacementTone(player, isRemake, arenaVictoryPlacement = 4) {
    if (isRemake) {
        return 'remake';
    }

    return toNumber(player?.placement) <= arenaVictoryPlacement ? 'victory' : 'defeat';
}

function formatDuration(seconds) {
    const durationSeconds = Math.max(0, Math.floor(toNumber(seconds)));
    const hours = Math.floor(durationSeconds / 3600);
    const minutes = Math.floor((durationSeconds % 3600) / 60);
    const remainingSeconds = durationSeconds % 60;

    if (hours > 0) {
        return `${hours}h ${minutes}m ${remainingSeconds}s`;
    }

    return `${minutes}m ${remainingSeconds}s`;
}

function parseStoredMatchElo(elo) {
    const normalizedElo = String(elo || '').trim().toUpperCase().replace(/\s+/g, ' ');

    if (!normalizedElo) {
        return null;
    }

    const exactTierMap = {
        UNRANKED: { badge: 'UR', label: 'UNRANKED' },
        MASTER: { badge: 'M', label: 'MASTER' },
        CHALLENGER: { badge: 'C', label: 'CHALLENGER' },
        GRANDMASTER: { badge: 'GM', label: 'GRAND MASTER' },
        'GRAND MASTER': { badge: 'GM', label: 'GRAND MASTER' },
    };

    if (exactTierMap[normalizedElo]) {
        return exactTierMap[normalizedElo];
    }

    const eloParts = normalizedElo.split(' ');
    const divisionPart = eloParts.pop();
    const tierPart = eloParts.join(' ');
    const tierMap = {
        IRON: { badge: 'I', label: 'IRON' },
        BRONZE: { badge: 'B', label: 'BRONZE' },
        SILVER: { badge: 'S', label: 'SILVER' },
        GOLD: { badge: 'G', label: 'GOLD' },
        PLATINUM: { badge: 'P', label: 'PLATINUM' },
        EMERALD: { badge: 'E', label: 'EMERALD' },
        DIAMOND: { badge: 'D', label: 'DIAMOND' },
    };

    if (!divisionPart || !tierMap[tierPart]) {
        return null;
    }

    return {
        badge: `${tierMap[tierPart].badge}${String(divisionPart).replace(/\D/g, '')}`,
        label: normalizedElo,
    };
}

function buildFallbackAverageEloDetails(elo) {
    const parsedAverageElo = parseStoredMatchElo(elo)

    if (!parsedAverageElo) {
        return {
            badge: 'UR',
            label: 'UNRANKED',
            shortLabel: 'U',
            isApex: false,
        }
    }

    const isApex = ['M', 'GM', 'C'].includes(parsedAverageElo.badge)
    const label = String(parsedAverageElo.label || '').trim().toUpperCase()
    const shortLabel = isApex
        ? parsedAverageElo.badge
        : label.split(' ').at(-1) || parsedAverageElo.badge

    return {
        badge: parsedAverageElo.badge,
        label,
        shortLabel,
        isApex,
    }
}

function formatTimeAgo(endTimestamp) {
    const timestamp = toNumber(endTimestamp);

    if (!timestamp) {
        return '';
    }

    const differenceInMinutes = Math.max(1, Math.round((Date.now() - timestamp) / 60000));

    if (differenceInMinutes < 60) {
        return `${differenceInMinutes} minute(s) ago`;
    }

    const differenceInHours = Math.floor(differenceInMinutes / 60);

    if (differenceInHours < 24) {
        return `${differenceInHours} hour(s) ago`;
    }

    return `${Math.floor(differenceInHours / 24)} day(s) ago`;
}

function buildParticipantViewModel(player, context) {
    const wrgg = player?.wrgg || {};
    const gameDurationSeconds = toNumber(context?.gameDurationSeconds);
    const gameDurationMinutes = gameDurationSeconds / 60;
    const totalCs = toNumber(player?.neutralMinionsKilled) + toNumber(player?.totalMinionsKilled);
    const csPerMinute = gameDurationMinutes > 0 ? totalCs / gameDurationMinutes : 0;
    const goldPerMinute = toNumber(player?.challenges?.goldPerMinute);
    const damageDealt = toNumber(player?.totalDamageDealtToChampions);
    const damageTaken = toNumber(player?.totalDamageTaken);
    const maxDamageDealt = Math.max(1, toNumber(context?.maxDamageDealt));
    const maxDamageTaken = Math.max(1, toNumber(context?.maxDamageTaken));
    const primaryStyle = player?.perks?.styles?.[0]?.style;
    const primaryPerk = player?.perks?.styles?.[0]?.selections?.[0]?.perk;
    const secondaryStyle = player?.perks?.styles?.[1]?.style;
    const transformedChampion = checkIfChampionIsKaynTransformation(player?.championName, player?.championTransform);
    const orderedItems = buildOrderedItems(player);
    const isRemake = context?.matchEndedUnexpected || Boolean(player?.gameEndedInEarlySurrender);
    const tone = getFocusTone(player, context?.isArena, isRemake, context?.arenaVictoryPlacement);
    const roleViewModel = getRoleViewModel(player?.teamPosition);
    const roleStatusLabel = getRoleStatusLabel(player);
    const isFocus = player?.puuid === context?.highlightedPuuid;
    const eloBadge = wrgg.elo?.badge || 'UR';
    const eloLabel = getEloString(player);

    return {
        puuid: player?.puuid,
        gameName: player?.riotIdGameName,
        tagLine: player?.riotIdTagline,
        displayName: `${player?.riotIdGameName || ''}#${player?.riotIdTagline || ''}`,
        linkSlug: `${player?.riotIdGameName || ''}-${player?.riotIdTagline || ''}`,
        teamId: toNumber(player?.teamId),
        placement: toNumber(player?.placement),
        placementLabel: toNumber(player?.placement) ? formatArenaPlacement(toNumber(player?.placement)) : '',
        playerSubteamId: toNumber(player?.playerSubteamId),
        arenaTeamName: ARENA_TEAM_NAMES[toNumber(player?.playerSubteamId)] || '',
        win: Boolean(player?.win),
        tone,
        arenaPlacementTone: getArenaPlacementTone(player, isRemake, context?.arenaVictoryPlacement),
        isRemake,
        isFocus,
        championId: toNumber(player?.championId),
        championName: transformedChampion,
        championIconSrc: toChampionUrl(transformedChampion),
        championLevel: toNumber(player?.champLevel),
        wrgg,
        performanceRank: context?.isCompleted && Number.isInteger(Number(wrgg.performance?.rank))
            ? Number(wrgg.performance.rank)
            : null,
        summoner1Id: toNumber(player?.summoner1Id),
        summoner2Id: toNumber(player?.summoner2Id),
        spellSources: [
            toSpellUrl(player?.summoner1Id),
            toSpellUrl(player?.summoner2Id),
        ],
        runeSources: {
            primary: toPrimaryRuneUrl(primaryStyle, primaryPerk),
            secondary: toSecondaryRuneUrl(secondaryStyle),
        },
        augmentSources: [
            toAugmentUrl(player?.playerAugment1),
            toAugmentUrl(player?.playerAugment2),
            toAugmentUrl(player?.playerAugment3),
            toAugmentUrl(player?.playerAugment4),
            toAugmentUrl(player?.playerAugment5),
            toAugmentUrl(player?.playerAugment6),
        ],
        itemSources: orderedItems.itemIds.map((itemId) => toItemUrl(itemId)),
        trinketSource: toItemUrl(orderedItems.trinketId),
        roleBoundItemSource: toItemUrl(orderedItems.roleBoundItemId),
        roleKey: roleViewModel.roleKey,
        roleLabel: roleViewModel.roleLabel,
        roleIconSrc: roleViewModel.roleIconSrc,
        roleStatusLabel,
        kills: toNumber(player?.kills),
        deaths: toNumber(player?.deaths),
        assists: toNumber(player?.assists),
        kda: toNumber(player?.challenges?.kda),
        isPerfectKda: Boolean(player?.challenges?.perfectGame) && toNumber(player?.challenges?.kda) !== 0,
        killParticipation: formatPercent(player?.challenges?.killParticipation),
        totalCs,
        csPerMinute,
        goldEarned: toNumber(player?.goldEarned),
        goldPerMinute,
        goldLabel: formatGoldValue(player?.goldEarned),
        damageDealt,
        damageTaken,
        damageDealtLabel: formatDamageValue(damageDealt),
        damageTakenLabel: formatDamageValue(damageTaken),
        damageDealtPercent: Math.round((damageDealt / maxDamageDealt) * 100),
        damageTakenPercent: Math.round((damageTaken / maxDamageTaken) * 100),
        visionScore: toNumber(player?.visionScore),
        controlWardsPlaced: toNumber(player?.challenges?.controlWardsPlaced),
        wardsPlaced: toNumber(player?.wardsPlaced),
        wardsKilled: toNumber(player?.wardsKilled),
        highestStreak: getHighestStreak(player),
        eloBadge,
        eloLabel,
        eloLeaguePoints: toNumber(wrgg.elo?.leaguePoints),
        summonerLevel: toNumber(wrgg.summonerLevel),
        buildPath: wrgg.buildPath || [],
        skillOrder: wrgg.skillOrder || [],
        evolutionOrder: wrgg.evolutionOrder || [],
        gameEndedInEarlySurrender: Boolean(player?.gameEndedInEarlySurrender),
    };
}

function buildArenaTeamViewModels(participants) {
    const arenaTeamsBySubteamId = new Map();

    participants.forEach((participant) => {
        const subteamId = toNumber(participant?.playerSubteamId);
        const existingTeam = arenaTeamsBySubteamId.get(subteamId);

        if (existingTeam) {
            existingTeam.participants.push(participant);
            existingTeam.placement = Math.min(existingTeam.placement, toNumber(participant?.placement) || 99);
            return;
        }

        arenaTeamsBySubteamId.set(subteamId, {
            subteamId,
            teamName: participant?.arenaTeamName || ARENA_TEAM_NAMES[subteamId] || '',
            placement: toNumber(participant?.placement) || 99,
            tone: participant?.arenaPlacementTone || participant?.tone || 'defeat',
            participants: [participant],
        });
    });

    return [...arenaTeamsBySubteamId.values()]
        .map((team) => ({
            ...team,
            placementLabel: team.placement > 0 ? formatArenaPlacement(team.placement) : '',
            participants: [...team.participants].sort((left, right) => {
                const placementDelta = toNumber(left?.placement) - toNumber(right?.placement);
                if (placementDelta !== 0) {
                    return placementDelta;
                }

                return String(left?.gameName || '').localeCompare(String(right?.gameName || ''));
            }),
        }))
        .sort((left, right) => left.placement - right.placement);
}

function buildTeamViewModel(teamId, participants, teamInfo, matchEndedUnexpected) {
    if (!participants.length) {
        return null;
    }

    const isWin = Boolean(teamInfo?.win ?? participants[0]?.win);
    const tone = matchEndedUnexpected ? 'remake' : isWin ? 'victory' : 'defeat';
    const teamKills = toNumber(teamInfo?.objectives?.champion?.kills) || participants.reduce((sum, participant) => sum + participant.kills, 0);

    return {
        teamId,
        label: teamId === 100 ? 'BLUE SIDE' : 'RED SIDE',
        outcomeLabel: matchEndedUnexpected ? 'REMAKE' : isWin ? 'VICTORY' : 'DEFEAT',
        tone,
        isWin,
        participants,
        kdaLine: `${teamKills} / ${participants.reduce((sum, participant) => sum + participant.deaths, 0)} / ${participants.reduce((sum, participant) => sum + participant.assists, 0)}`,
        objectives: {
            turrets: toNumber(teamInfo?.objectives?.tower?.kills),
            dragons: toNumber(teamInfo?.objectives?.dragon?.kills),
            voidGrubs: toNumber(teamInfo?.objectives?.horde?.kills),
            riftHeralds: toNumber(teamInfo?.objectives?.riftHerald?.kills),
            barons: toNumber(teamInfo?.objectives?.baron?.kills),
            inhibitors: toNumber(teamInfo?.objectives?.inhibitor?.kills),
        },
        averageElo: teamInfo?.wrgg?.averageElo || null,
        bans: Array.isArray(teamInfo?.bans)
            ? teamInfo.bans
                .map((ban) => ({
                    championId: toNumber(ban?.championId),
                    pickTurn: toNumber(ban?.pickTurn),
                }))
                .sort((left, right) => left.pickTurn - right.pickTurn)
            : [],
    };
}

function splitParticipantsByTeam(participants) {
    return {
        blueSide: participants.filter((participant) => participant.teamId === 100),
        redSide: participants.filter((participant) => participant.teamId === 200),
    };
}

export function getGameModeLabel(queueId) {
    return QUEUE_LABELS[toNumber(queueId)] || 'Unknown Game Mode';
}

export function getCompactGameModeLabel(gameModeLabel) {
    const normalizedLabel = String(gameModeLabel || '').trim().toUpperCase();

    if (normalizedLabel === 'SOLO RANKED' || normalizedLabel === 'RANKED SOLO' || normalizedLabel === 'SOLO/DUO' || normalizedLabel === 'SOLO/DUO QUEUE') {
        return 'SOLO/DUO';
    }

    if (normalizedLabel === 'ULT SPELLBOOK' || normalizedLabel === 'ULTIMATE SPELLBOOK') {
        return 'ULT. SPELL.';
    }

    if (normalizedLabel === 'NEXUS BLITZ') {
        return 'N. BLITZ';
    }

    if (normalizedLabel === 'QUICK PLAY') {
        return 'QUICK';
    }

    if (normalizedLabel === '(N) TWISTED TREELINE' || normalizedLabel === 'TWISTED TREELINE') {
        return 'TT FLEX';
    }

    if (normalizedLabel === 'ARAM CLASH') {
        return 'ARAM CLS';
    }

    return normalizedLabel;
}

export function createMatchEntryViewModel(matchRecord, summonerPuuid) {
    if (!matchRecord?.archived_match_json || !summonerPuuid) {
        return null;
    }

    const rawMatch = matchRecord.archived_match_json;
    const matchInfo = rawMatch?.info;
    const rawParticipants = matchInfo?.participants || [];
    const highlightedParticipant = rawParticipants.find((participant) => participant?.puuid === summonerPuuid);

    if (!highlightedParticipant) {
        return null;
    }

    const queueId = toNumber(matchRecord?.queue_id || matchInfo?.queueId);
    const isArena = isArenaQueue(queueId);
    const isTriosArena = isTriosArenaQueue(queueId);
    const arenaVictoryPlacement = getArenaVictoryPlacement(queueId);
    const isCompleted = matchRecord?.completion_status
        ? matchRecord.completion_status === 'completed'
        : matchInfo?.endOfGameResult === 'GameComplete'
            && !rawParticipants.some((participant) => participant?.gameEndedInEarlySurrender);
    const matchEndedUnexpected = matchInfo?.endOfGameResult === 'Abort_Unexpected';
    const maxDamageDealt = rawParticipants.reduce((maximum, participant) => Math.max(maximum, toNumber(participant?.totalDamageDealtToChampions)), 0);
    const maxDamageTaken = rawParticipants.reduce((maximum, participant) => Math.max(maximum, toNumber(participant?.totalDamageTaken)), 0);

    const participants = rawParticipants.map((participant) => buildParticipantViewModel(participant, {
        gameDurationSeconds: matchInfo?.gameDuration,
        highlightedPuuid: summonerPuuid,
        isArena,
        isCompleted,
        arenaVictoryPlacement,
        matchEloLabel: matchRecord?.display_elo || rawMatch?.wrgg?.averageEloLabel,
        matchEndedUnexpected,
        maxDamageDealt,
        maxDamageTaken,
    }));

    const player = participants.find((participant) => participant.puuid === summonerPuuid);

    if (!player) {
        return null;
    }

    const { blueSide, redSide } = splitParticipantsByTeam(participants);
    const teams = isArena
        ? []
        : [
            buildTeamViewModel(100, blueSide, matchInfo?.teams?.find((team) => toNumber(team?.teamId) === 100), matchEndedUnexpected || player.gameEndedInEarlySurrender),
            buildTeamViewModel(200, redSide, matchInfo?.teams?.find((team) => toNumber(team?.teamId) === 200), matchEndedUnexpected || player.gameEndedInEarlySurrender),
        ].filter(Boolean);

    const arenaParticipants = isArena
        ? [...participants].sort((left, right) => left.placement - right.placement)
        : [];
    const arenaTeams = isArena ? buildArenaTeamViewModels(arenaParticipants) : [];

    const topArenaParticipants = arenaParticipants.filter((participant) => participant.placement > 0).slice(0, arenaVictoryPlacement);
    const topArenaTeams = arenaTeams.filter((team) => team.placement > 0).slice(0, arenaVictoryPlacement);
    const averageEloLabel = matchRecord?.display_elo || rawMatch?.wrgg?.averageEloLabel || '';
    const averageEloDetails = rawMatch?.wrgg?.averageElo || buildFallbackAverageEloDetails(averageEloLabel);

    return {
        id: rawMatch?.matchId || matchRecord?.match_id,
        footerMatchId: matchRecord?.match_id || rawMatch?.matchId,
        queueId,
        isArena,
        isTriosArena,
        arenaVictoryPlacement,
        completionStatus: matchRecord?.completion_status || (isCompleted ? 'completed' : 'unknown'),
        isCompleted,
        isRemake: player.isRemake,
        tone: player.tone,
        statusLabel: player.isRemake ? 'REMAKE' : player.tone === 'victory' ? 'VICTORY' : 'DEFEAT',
        gameModeLabel: getGameModeLabel(queueId),
        averageEloLabel,
        reportingTier: matchRecord?.reporting_tier || rawMatch?.wrgg?.averageTier || '',
        averageEloBadge: averageEloDetails?.badge || 'UR',
        averageEloDisplay: averageEloDetails?.label || '',
        averageEloDetails,
        teamState: rawMatch?.wrgg?.teamState || null,
        timeAgoLabel: formatTimeAgo(matchInfo?.gameEndTimestamp),
        durationLabel: formatDuration(matchInfo?.gameDuration),
        player,
        playerRaw: highlightedParticipant,
        rawMatch,
        teams,
        arenaParticipants,
        arenaTeams,
        topArenaTeams,
        previewColumns: isArena
            ? [
                topArenaParticipants.filter((_, index) => index % 2 === 0),
                topArenaParticipants.filter((_, index) => index % 2 === 1),
            ]
            : [blueSide, redSide],
    };
}
